import { loadSaveFilterIdPrefix } from "@/utils/DisplayPaneConstants";
import i18n from "@/utils/i18n";
import html2canvas from "html2canvas";
import { defineStore } from "pinia";
import { useSsaStore as ssa } from ".";
import config from "../../../public/config.json";
import { useApiStore as api } from "../apiStore";
import { useHierarchyStore } from "../hierarchyStore";
import { usePanelStore } from "../panelStore";
import { useToastStore as toast } from "../toastStore";
import { useActiveStore as active, ISelectedDatasets } from "./active";
import { useDisplayPaneStore } from "./displayPane";
import { useFiltersStore as filters, FiltersMenuState } from "./filters";
import { usePvwStore as pvw } from "./pvw";
import { useViewStore as views } from "./views";

let CURRENT_STATE: any = null;
let NEXT_STATE_ID: any = 1;
const DEFAULT_VIEW_ID = config.paraview_defaults.defaultViewId;
const BLANK_STATE: any = JSON.parse(
	`{"name":"blank_slate","id":1,"clientState":{"activeView":${DEFAULT_VIEW_ID},"activeLayerByView":{},"links":[{"viewId":${DEFAULT_VIEW_ID},"link":0}]},"loadedPieces":{},"layout":[{"id":0,"component":"splitpanes","horizontal":false,"content":[{"id":1,"component":"SsaOverlayView","viewId":"${DEFAULT_VIEW_ID}","viewType":"3D","linked":0}]}],"views":{"${DEFAULT_VIEW_ID}":{"camera":{"CenterOfRotation":[0,0,0],"CameraFocalPoint":[0,0,0],"CameraParallelProjection":0,"CameraParallelScale":1.73,"CameraPosition":[0,0,6.69],"CameraViewAngle":30,"CameraViewUp":[0,1,0]},"view":{"zScaling":1,"interaction":"3D","background":[0.09,0.09,0.09],"foreground":[0.91,0.91,0.91],"orientationAxesVisibility":1,"cameraParallelProjection":0,"centerAxesVisibility":0,"gridVisibility":0,"linked":0,"viewId":"${DEFAULT_VIEW_ID}","type":"3D"},"layers":{}}},"charts":{}}`
);
export const useSaveStateStore = defineStore("saveStateStore", {
	state: () =>
		({
			ts: 0,
			activeSaveState: null,
			savingState: false,
			loadingState: false
		}) as any,
	getters: {
		SSA_SAVING_STATE(): any {
			return this.savingState;
		},
		SSA_ACTIVE_SAVE_STATE(): any {
			return this.activeSaveState;
		},
		SSA_APPLICATION_CURRENT_STATE(): any {
			return CURRENT_STATE;
		},
		SSA_APPLICATION_CLIENT_STATE(): any {
			const links: any = [];
			Object.entries(views().SSA_VIEW_STATES).forEach(([viewId, value]) => {
				links.push({ viewId, link: (value as any).linked });
			});
			return {
				activeView: active().SSA_ACTIVE_VIEW,
				activeLayerByView: active().SSA_ACTIVE_LAYER_BY_VIEW,
				links
			};
		}
	},
	actions: {
		TRIGGER_RELOAD() {
			this.savingState = true;
			setTimeout(() => {
				this.savingState = false;
			}, 600);
		},
		async SAVE_ACTIVE_STATE() {
			this.savingState = true;
			try {
				const createThumbnail = async () => {
					try {
						const vizEl = (document as any).getElementById("_viz");
						const options = {
							allowTaint: true,
							useCORS: true,
							height: vizEl.clientHeight,
							width: vizEl.clientWidth
						};
						const canvas = await html2canvas(vizEl, options);
						const imageDataUrl = canvas.toDataURL("image/jpeg", 0.95);
						const response = await fetch(imageDataUrl);
						const blob = await response.blob();
						const formData = new FormData();
						formData.append("file", blob, "screenshot.jpg");

						// Upload to server
						return imageDataUrl;
					} catch (error) {
						this.savingState = false;
						console.error("Error creating thumbnail:", error);
					}
				};
				const thumbUrl = await createThumbnail();
				const getBase64Image = (img: any) => {
					const canvas = document.createElement("canvas");
					canvas.width = img.width * 2;
					canvas.height = img.height * 2;
					const ctx: any = canvas.getContext("2d");
					ctx.drawImage(img, 0, 0, 250 * 2, 150 * 2);
					const dataURL = canvas.toDataURL("image/jpeg");
					return dataURL;
				};

				const imgEl: any = new Image(250, 150);
				imgEl.src = thumbUrl;

				imgEl.onload = async () => {
					try {
						const thumb = getBase64Image(imgEl);
						const d = await this.SSA_APPLICATION_STATE_FETCH();
						const state = {
							...this.activeSaveState,
							appState: d,
							thumbnail: thumb
						};

						api().put({
							endpoint: `/state/update/${state.id}`,
							post: { appState: state.appState, thumbnail: state.thumbnail }
						});
						this.SSA_SET_ACTIVE_SAVE_STATE(state);
						this.savingState = false;
						return state;
					} catch (e: any) {
						toast().toast({
							title: e.message,
							message: "",
							dismissable: false,
							type: "error"
						});
						this.savingState = false;
					}
				};
				this.savingState = false;
			} catch (e) {
				this.savingState = false;
				toast().toast({
					message: i18n.global.t("storeSaveStateToasts.errorUpdatingState"),
					title: i18n.global.t("storeSaveStateToasts.appError"),
					type: "error",
					dismissable: false
				});
			}
		},
		async LOAD_BLANK_STATE() {
			// TODO
			CURRENT_STATE = BLANK_STATE;
			this.ts++;
			ssa()
				.SSA_RESET_SESSION()
				.then(() => {
					this.SSA_APPLICATION_STATE_LOAD();
				});
		},
		async SSA_APPLICATION_STATE_FETCH() {
			const layout = JSON.parse(JSON.stringify(usePanelStore().PANEL_LAYOUT));
			const displayPaneState = JSON.parse(
				JSON.stringify(useDisplayPaneStore().DISPLAY_STATES)
			);
			const hierarchyState = JSON.parse(JSON.stringify(useHierarchyStore().HIERARCHY_STATE));
			const hierarchyLayerSelections = JSON.parse(
				JSON.stringify(useHierarchyStore().HIERARCHY_LAYER_SELECTIONS)
			);
			if (active().SSA_ACTIVE_DATASET == null) {
				throw new Error("Cannot save a state with no active dataset.");
			}
			const activeDataset = JSON.parse(JSON.stringify(active().SSA_ACTIVE_DATASET));
			const d = await this.SSA_APPLICATION_GET_STATE("test1");

			const filtersMapByDataset = JSON.parse(
				JSON.stringify(filters().SSA_ACTIVE_FILTERS_MAP_BY_DATASET)
			); // to avoid modifying the existing filters in place

			// find any filters for which the layer is unselected
			for (const viewId of Object.keys(filtersMapByDataset)) {
				for (const datasetId of Object.keys(filtersMapByDataset[viewId])) {
					for (const filter of filtersMapByDataset[viewId][datasetId].aggregatedFilters) {
						filter.id = loadSaveFilterIdPrefix + filter.id; //  break existing ids; this is to avoid conflicts with the new filters created when loading the state

						const layer = displayPaneState[viewId][datasetId].find(
							(layer: any) =>
								layer.layerName === filter.layerName && layer.selected === false
						);
						if (layer) {
							// if the layer is unselected, add a flag to the filter indicating it needs to be manually added for the layer on layer load, and ignored on "unselect filters"
							filter.layerUnselectedOnSaveState = true;
						}
					}
				}
			}

			const filtersDisplayPaneState = filters().SSA_ALL_ACTIVE_FILTERS_STATE;

			CURRENT_STATE = {
				...d,
				loadedPieces: hierarchyLayerSelections,
				layout,
				displayPaneState,
				hierarchyState,
				hierarchyLayerSelections,
				activeDataset,
				filtersMapByDataset,
				filtersDisplayPaneState
			};

			this.ts++;
			return CURRENT_STATE;
		},
		async SSA_APPLICATION_STATE_ACTIVATE(name: any) {
			this.SSA_ACTIVE_STATE_NAME_SET(name);
			CURRENT_STATE = this.applicationStates[name];
			this.ts++;
			return this.SSA_APPLICATION_STATE_LOAD();
		},
		SSA_SET_ACTIVE_SAVE_STATE(state: any) {
			this.activeSaveState = state;
		},
		async SSA_APPLICATION_STATE_LOAD(state: any) {
			this.loadingState = true;
			usePanelStore().SET_READY(false);
			this.savingState = true;
			const appState = state.appState;
			this.SSA_SET_ACTIVE_SAVE_STATE(state);

			if (this.loading) {
				return;
			}
			try {
				// await ssa().SSA_RESET_SESSION();
				this.loading = true;
				const temp_current_state = appState;
				let layout = appState.layout;
				const displayPaneState = appState.displayPaneState;

				for (const viewId of Object.keys(displayPaneState)) {
					for (const datasetId of Object.keys(displayPaneState[viewId])) {
						for (const layer of displayPaneState[viewId][datasetId]) {
							if (layer.selected === false && layer.defaults_applied === true) {
								layer.displayPaneLayerUnselectedOnSaveState = true;
							}
						}
					}
				}

				const hierarchyState = appState.hierarchyState;
				const hierarchyLayerSelections = appState.hierarchyLayerSelections;
				const storedActiveDataset = appState.activeDataset;
				const filtersMapByDataset = appState.filtersMapByDataset;
				const filtersDisplayPaneState = appState.filtersDisplayPaneState;
				// Look up the real dataset in the hierarchy - the stored one
				// could be out of date
				const id: string = storedActiveDataset.id;
				const allDatasets = useHierarchyStore().ALL_DATASETS();
				const currentDataset = allDatasets.find(
					(dataset: { id: string }) => dataset.id == id
				);
				const activeDataset = currentDataset ? currentDataset : storedActiveDataset;
				active().SSA_SET_ACTIVE_DATASET(activeDataset);

				for (const [viewId, viewState] of Object.entries(hierarchyLayerSelections)) {
					views().SSA_SET_PARAVIEW_VIEW_STATE(viewId, viewState as any);
				}

				const {
					clientState,
					idMapping
				}: { clientState: any; idMapping: { [oldViewId: string]: string } } =
					await pvw().PVW_APPLICATION_STATE_SET({
						...temp_current_state,
						viewIds: Object.keys(hierarchyState),
						layout: BLANK_STATE.layout
					});

				// Generate selectedDatasets from activeStore using hierarchyState and remap the view ids
				const selectedDatasetsRemappedViewIds = {} as ISelectedDatasets;
				for (const [oldViewId, selectedHierarchyStrings] of Object.entries(
					hierarchyState
				)) {
					const selectedHierarchyStringArray = (selectedHierarchyStrings as any).selected;
					selectedHierarchyStringArray.forEach((select: string) => {
						const datasetId = select.split("..")[0];
						if (!selectedDatasetsRemappedViewIds[idMapping[oldViewId]])
							selectedDatasetsRemappedViewIds[idMapping[oldViewId]] = {};
						selectedDatasetsRemappedViewIds[idMapping[oldViewId]][datasetId] = true;
					});
				}
				// updated activeStore's selectedDatasets
				active().SSA_SET_SELECTED_ON_STATE_LOAD(selectedDatasetsRemappedViewIds);

				const replaceViewIds = (target: any) => {
					if (typeof target === "object") {
						for (const key in target) {
							if (typeof target[key] === "object") {
								replaceViewIds(target[key]);
							} else {
								if (key === "viewId") {
									target[key] = idMapping[target[key]];
								}
							}
						}
					}
					return target;
				};

				const newDisplayPaneState = {} as any;
				const newHState = {} as any;
				const newHLayerSelections = {} as any;
				const newFiltersMapByDataset = {} as any;

				const newFiltersDisplayPaneState = {} as FiltersMenuState;
				for (const [key, mappedKey] of Object.entries(idMapping)) {
					newDisplayPaneState[mappedKey] = displayPaneState[key];
					newHState[mappedKey] = hierarchyState[key];
					newHLayerSelections[mappedKey] = hierarchyLayerSelections[key];
					newFiltersMapByDataset[mappedKey] = filtersMapByDataset[key];
					newFiltersDisplayPaneState[mappedKey] = filtersDisplayPaneState[key];
				}

				layout = replaceViewIds(layout);

				await this.SSA_APPLICATION_CLIENT_STATE_LOAD({
					...clientState,
					viewIds: clientState.viewIds
				});

				const viewId = appState.clientState.activeView;

				const allSelectedLayers = displayPaneState[viewId][
					appState.activeDataset.id
				]?.filter((layer: any) => {
					return layer.selected;
				});

				allSelectedLayers.forEach((layer: any) => {
					if (layer.open) {
						active().SSA_ACTIVE_LAYER_SET(layer.layerName);
					}

					const layerName = layer.layerName;
					let fieldList: any = ["hole_name", "bench_name", "pattern_name"];

					if (
						layer.configuration.length > 0 &&
						layer.configuration[0].availableProperties
					) {
						fieldList = fieldList.concat(
							layer.configuration[0].availableProperties[0].children.map(
								(item: any) => {
									return item.value;
								}
							)
						);
					}
					if (
						!layer.name.toLowerCase().includes("survey") &&
						!layer.name.toLowerCase().includes("slic")
					) {
						pvw().PVW_SET_FIELD_LIST({ layerName, viewId, fieldList });
					}
				});

				useDisplayPaneStore().SET_DISPLAY_PANE_STATE(newDisplayPaneState);

				views().SSA_VIEW_IDS_SET(Object.values(idMapping));
				useHierarchyStore().SET_HIERARCHY_STATE(newHState, newHLayerSelections);

				filters().SSA_LOAD_FILTERS_FROM_SAVE(newFiltersMapByDataset);
				filters().SSA_LOAD_FILTERS_DISPLAY_STATE(newFiltersDisplayPaneState);

				usePanelStore().SET_PANEL_LAYOUT(layout);
				usePanelStore().SET_READY(true);
				window.dispatchEvent(new Event("resize"));
				const idToActivate = [clientState.activeView].concat(
					Object.keys(clientState.activeLayerByView)
				);

				const activateId = () => {
					if (idToActivate.length) {
						active().SSA_ACTIVE_VIEW_SET(idToActivate.pop());
						setTimeout(activateId, 0);
					}
				};
				setTimeout(activateId, 1);
			} finally {
				this.loading = false;
				this.savingState = false;
				this.loadingState = false;
			}
		},
		async SSA_APPLICATION_GET_STATE(name: any) {
			return new Promise((resolve) => {
				pvw()
					.PVW_APPLICATION_STATE_GET()
					.then((state: any) => {
						const content = JSON.stringify(
							Object.assign(
								{
									name,
									id: NEXT_STATE_ID++,
									clientState: this.SSA_APPLICATION_CLIENT_STATE
								},
								state
							)
						);
						const obj: any = {};
						obj[name] = content;
						resolve(JSON.parse(content));
					});
			});
		},
		async SSA_APPLICATION_CLIENT_STATE_LOAD(clientState: any) {
			await active().SSA_ACTIVE_VIEW_SET(clientState.activeView || "");
			await active().SSA_ACTIVE_LAYER_BY_VIEW_SET(clientState.activeLayerByView || {});
		}
	}
});
